import { createSlice } from '@reduxjs/toolkit'
import MarketplaceIcon from 'Components/Svg/MarketplaceIcon'
import SitesIcon from 'Components/Svg/SitesIcon'
import MarketsIcon from 'Components/Svg/MarketsIcon'
import { IsSetCurrentRuleSelected } from '../../Constants/Configuration'
import {
  obtainBrandsFromData,
  obtainDiscountElementsFromData,
  obtainPriceDiscount,
  obtainPriceListFromData,
} from '../../Helpers/DiscountHelper'

export enum TypeOfChannel {
  Marketplace,
  Site,
  Store,
}

export const iconDictionary = {
  [TypeOfChannel.Marketplace]: MarketplaceIcon,
  [TypeOfChannel.Site]: SitesIcon,
  [TypeOfChannel.Store]: MarketsIcon,
}

export const discountSlice = createSlice({
  name: 'discount',
  initialState: {
    discountData: {},
    discountToSave: [],
    brandsSelected: [],
    listingCopyPopoverSelected: { superGroup: null, sector: null, channel: null },
    brandSearch: '',
    loading: false,
    priceListToCopyFrom: {} as any,
    priceListsToCopyTo: [],
    cleaningPopup: false,
    typeOfChannel: TypeOfChannel.Marketplace,
  },
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setDiscountData: (state, action) => {
      state.discountData = action.payload

      state.discountToSave = []
    },
    setDiscountToSave: (state, action) => {
      const savedDiscountsWithoutOlderDiscount = state.discountToSave.filter(
        x =>
          !(
            x.superGroup === action.payload.superGroup &&
            x.sector === action.payload.sector &&
            x.channel === action.payload.channel &&
            x.brand === action.payload.brand
          ),
      )

      const filteredDiscountsByInheritanceValue =
        action.payload.brand && action.payload.discount
          ? savedDiscountsWithoutOlderDiscount.filter(
              x =>
                !(
                  x.superGroup === action.payload.superGroup &&
                  x.sector === action.payload.sector &&
                  x.channel === action.payload.channel &&
                  x.brand === action.payload.brand
                ),
            )
          : action.payload.channel && action.payload.discount
          ? savedDiscountsWithoutOlderDiscount.filter(
              x =>
                !(
                  x.superGroup === action.payload.superGroup &&
                  x.sector === action.payload.sector &&
                  x.channel === action.payload.channel &&
                  x.brand &&
                  x.discount === action.payload.discount
                ),
            )
          : action.payload.sector && action.payload.discount
          ? savedDiscountsWithoutOlderDiscount.filter(
              x =>
                !(
                  x.superGroup === action.payload.superGroup &&
                  x.sector === action.payload.sector &&
                  x.channel &&
                  x.discount === action.payload.discount
                ),
            )
          : !action.payload.superGroup && action.payload.discount
          ? savedDiscountsWithoutOlderDiscount.filter(x => !(x.discount === action.payload.discount))
          : savedDiscountsWithoutOlderDiscount

      filteredDiscountsByInheritanceValue.push(action.payload) //new discount pushed

      state.discountToSave = filteredDiscountsByInheritanceValue
    },
    removeDiscountToSave: (state, action) => {
      state.discountToSave = state.discountToSave.filter(
        x =>
          !(
            x.superGroup === action.payload.superGroup &&
            x.sector === action.payload.sector &&
            x.channel === action.payload.channel &&
            x.brand === action.payload.brand
          ),
      )
    },
    setelectedPricelist: (state, action) => {
      state.discountToSave = state.discountToSave.filter(x => !x.channel || action.payload.indexOf(x.channel) !== -1)
    },
    setSelectedBrands: (state, action) => {
      const isLocked = state.brandsSelected.indexOf(action.payload) !== -1

      if (isLocked) {
        state.brandsSelected = state.brandsSelected.filter(x => x !== action.payload)
        return
      }

      state.brandsSelected.push(action.payload)
    },
    resetSelectedBrands: state => {
      state.brandsSelected = []
    },
    setBrandSearch: (state, action) => {
      state.brandSearch = action.payload
    },
    setActivePriceListToBeCopy: (state, action) => {
      state.priceListToCopyFrom = action.payload ? action.payload : {}
    },
    setPriceListsToCopyTo: (state, action) => {
      const isChannelInList = state.priceListsToCopyTo.find(
        x =>
          x.superGroup === action.payload.superGroup &&
          x.sector === action.payload.sector &&
          x.channel === action.payload.channel,
      )

      if (isChannelInList) {
        const channelIndex = state.priceListsToCopyTo.findIndex(
          x =>
            x.superGroup === action.payload.superGroup &&
            x.sector === action.payload.sector &&
            x.channel === action.payload.channel,
        )
        if (channelIndex !== -1) {
          state.priceListsToCopyTo.splice(channelIndex, 1)
        }
      } else {
        state.priceListsToCopyTo.push(action.payload)
      }
    },
    resetPriceListsToCopyTo: state => {
      state.priceListsToCopyTo = []
    },
    doPriceListCopy: state => {
      if (state.priceListsToCopyTo.length > 0 && Object.keys(state.priceListToCopyFrom).length > 0) {
        const discountToSaveCopy = [...state.discountToSave]
        const discountFilteredWithoutPriceListCopyTo = discountToSaveCopy.filter(
          x =>
            state.priceListsToCopyTo.findIndex(
              y => x.superGroup === y.superGroup && x.sector === y.sector && x.channel === y.channel,
            ) === -1,
        )

        const dataActivePriceList = obtainPriceListFromData(
          state.discountData,
          state.priceListToCopyFrom.superGroup,
          state.priceListToCopyFrom.sector,
          state.priceListToCopyFrom.channel,
        )

        const priceListCopyFromDiscount = obtainPriceDiscount(
          discountFilteredWithoutPriceListCopyTo,
          state.priceListToCopyFrom.superGroup,
          state.priceListToCopyFrom.sector,
          state.priceListToCopyFrom.channel,
          dataActivePriceList,
        )

        state.priceListsToCopyTo.forEach(priceList => {
          const mappedChannel = {
            superGroup: priceList.superGroup,
            sector: priceList.sector,
            channel: priceList.channel,
            brand: null,
            discount: priceListCopyFromDiscount,
          }

          discountFilteredWithoutPriceListCopyTo.push(mappedChannel)

          const filteredBrandsByCurrentListingToOverride = obtainBrandsFromData(
            state.discountData,
            priceList.superGroup,
            priceList.sector,
            priceList.channel,
          )

          filteredBrandsByCurrentListingToOverride.forEach(brand => {
            const selectedListingBrandValue = dataActivePriceList.brandDiscounts.find(
              x => x.brand.id === brand.brand.id,
            )
            const savedBrandValue = discountFilteredWithoutPriceListCopyTo.find(
              x =>
                x.superGroup === state.priceListToCopyFrom.superGroup &&
                x.sector === state.priceListToCopyFrom.sector &&
                x.channel === state.priceListToCopyFrom.channel &&
                x.brand === brand.brand.id,
            )

            const mappedBrand = {
              superGroup: priceList.superGroup,
              sector: priceList.sector,
              channel: priceList.channel,
              brand: brand.brand.id,
              discount: savedBrandValue
                ? savedBrandValue.discount
                : selectedListingBrandValue
                ? selectedListingBrandValue.discount
                : null,
            }

            discountFilteredWithoutPriceListCopyTo.push(mappedBrand)
          })
        })

        state.discountToSave = discountFilteredWithoutPriceListCopyTo
      }
    },
    setListingCopyPopover: (state, action) => {
      state.listingCopyPopoverSelected = action.payload
    },
    clearDiscounts: state => {
      state.discountToSave = []
      state.discountToSave.push({ discount: 0, cleared: true })
      const idItems = obtainDiscountElementsFromData(state.discountData)
      idItems.forEach(element => {
        state.discountToSave.push({ ...element, discount: null, cleared: true })
      })
    },
    resetDiscounts: state => {
      state.discountToSave = []
    },
    cleaningPopup: (state, action) => {
      state.cleaningPopup = action.payload
    },
    setTypeOfChannel: (state, action) => {
      state.typeOfChannel = action.payload
    },
  },
  extraReducers: builder => {
    builder.addMatcher(IsSetCurrentRuleSelected, (state, action) => {
      state.brandsSelected = []
      state.brandSearch = ''
    })
  },
})

export const {
  setLoading,
  setDiscountData,
  setDiscountToSave,
  setSelectedBrands,
  resetSelectedBrands,
  setBrandSearch,
  removeDiscountToSave,
  setActivePriceListToBeCopy,
  setPriceListsToCopyTo,
  resetPriceListsToCopyTo,
  doPriceListCopy,
  setListingCopyPopover,
  clearDiscounts,
  cleaningPopup,
  resetDiscounts,
  setTypeOfChannel,
} = discountSlice.actions

export default discountSlice.reducer
